<script setup lang="ts">
import {
    QLC_MATRIX_ALGORITHMS,
    DEFAULT_ALGORITHM_IDS,
    getAlgorithmById,
} from '../utils/matrixAlgorithms';

interface Props {
    widgetId: string;
    name?: string;
    color?: string;
}

const props = withDefaults(defineProps<Props>(), {
    name: 'RGB Matrix',
    color: '#ff007f',
});

const {
    broadcastGlobalColors,
    setMatrixAlgorithm,
    setMatrixPower,
    triggerHaptic,
    qlcStore,
} = useQlcPlus();

interface ColorTheme {
    id: string;
    name: string;
    color1: string;
    color2: string;
    icon?: string;
}

// Dancefloor-tested master dual-color themes
const themes: ColorTheme[] = [
    { id: 'cyberpunk', name: 'Cyberpunk', color1: '#d500f9', color2: '#00f0ff' },
    { id: 'fire-ice', name: 'Fire & Ice', color1: '#ff1744', color2: '#00e5ff' },
    { id: 'toxic-acid', name: 'Toxic Acid', color1: '#00e676', color2: '#7c4dff' },
    { id: 'sunset-glow', name: 'Sunset Glow', color1: '#ff6d00', color2: '#2979ff' },
    { id: 'lava-blaze', name: 'Lava Blaze', color1: '#ffd600', color2: '#dd2c00' },
    { id: 'deep-synth', name: 'Deep Synth', color1: '#651fff', color2: '#00e5ff' },
    { id: 'golden-warm', name: 'Golden Warm', color1: '#ffffff', color2: '#ffab40' },
    { id: 'candy-pop', name: 'Candy Pop', color1: '#ff1744', color2: '#ffffff' },
];

// Single color shortcuts
const solidColors = [
    { name: 'Rood', hex: '#ff0000' },
    { name: 'Groen', hex: '#00ff00' },
    { name: 'Blauw', hex: '#0000ff' },
    { name: 'Cyaan', hex: '#00ffff' },
    { name: 'Magenta', hex: '#ff00ff' },
    { name: 'Geel', hex: '#ffff00' },
    { name: 'Wit', hex: '#ffffff' },
];

// Visible algorithms in quick bar (user customizable)
const visibleAlgorithmIds = ref<number[]>([...DEFAULT_ALGORITHM_IDS]);
const activeAlgorithmId = ref<number>(34); // 34 = One By One

// Manage algorithms modal
const showAlgoDialog = ref(false);
const algoSearchQuery = ref('');

const filteredAlgorithms = computed(() => {
    const q = algoSearchQuery.value.trim().toLowerCase();
    if (!q) {
        return QLC_MATRIX_ALGORITHMS;
    }
    return QLC_MATRIX_ALGORITHMS.filter(a =>
        a.name.toLowerCase().includes(q) || String(a.id).includes(q),
    );
});

const color1 = ref('#d500f9');
const color2 = ref('#00f0ff');
const activeThemeId = ref('cyberpunk');

const color1InputRef = ref<HTMLInputElement | null>(null);
const color2InputRef = ref<HTMLInputElement | null>(null);

// All animation widgets currently discovered
const availableMatrices = computed(() => {
    return Object.values(qlcStore.widgets).filter(w => w.type === 'Animation');
});

// Currently selected targets for color sync (defaults strictly to props.widgetId)
const selectedTargetIds = ref<string[]>([props.widgetId]);

watch(
    () => props.widgetId,
    (newId) => {
        if (newId && !selectedTargetIds.value.includes(newId)) {
            selectedTargetIds.value = [newId];
        }
    },
    { immediate: true },
);

// Toggle a matrix in/out of the sync group
function toggleTarget(id: string): void {
    triggerHaptic(15);
    const idx = selectedTargetIds.value.indexOf(id);
    if (idx > -1) {
        if (selectedTargetIds.value.length > 1) {
            selectedTargetIds.value.splice(idx, 1);
        }
    }
    else {
        selectedTargetIds.value.push(id);
        broadcastGlobalColors(color1.value, color2.value, [id]);
    }
}

// Broadcast current colors strictly to selected targets
function applyColors(): void {
    const targets = selectedTargetIds.value.length > 0 ? selectedTargetIds.value : [props.widgetId];
    broadcastGlobalColors(color1.value, color2.value, targets);
}

// Select a curated dual-color theme
function handleSelectTheme(theme: ColorTheme): void {
    triggerHaptic(20);
    activeThemeId.value = theme.id;
    color1.value = theme.color1;
    color2.value = theme.color2;
    applyColors();
}

// Select a solid single color (sets both slots to identical color)
function handleSelectSolidColor(hex: string): void {
    triggerHaptic(15);
    activeThemeId.value = '';
    color1.value = hex;
    color2.value = hex;
    applyColors();
}

// Swap color 1 and color 2
function handleSwapColors(): void {
    triggerHaptic(20);
    const temp = color1.value;
    color1.value = color2.value;
    color2.value = temp;
    activeThemeId.value = '';
    applyColors();
}

// Switch matrix pattern algorithm for all selected matrices
function handleSelectAlgorithm(algoId: number): void {
    triggerHaptic(20);
    activeAlgorithmId.value = algoId;
    const targets = selectedTargetIds.value.length > 0 ? selectedTargetIds.value : [props.widgetId];
    for (const targetId of targets) {
        setMatrixAlgorithm(targetId, algoId);
    }
}

// Toggle an algorithm in/out of quick buttons
function toggleFavoriteAlgorithm(id: number): void {
    triggerHaptic(15);
    const idx = visibleAlgorithmIds.value.indexOf(id);
    if (idx > -1) {
        if (visibleAlgorithmIds.value.length > 1) {
            visibleAlgorithmIds.value.splice(idx, 1);
        }
    }
    else {
        visibleAlgorithmIds.value.push(id);
    }
    if (typeof window !== 'undefined') {
        try {
            localStorage.setItem('qlc_matrix_algo_favorites', JSON.stringify(visibleAlgorithmIds.value));
        }
        catch {
            // Ignore localStorage errors
        }
    }
}

// Reset quick buttons to default 6 algorithms
function resetDefaultAlgorithms(): void {
    triggerHaptic(20);
    visibleAlgorithmIds.value = [...DEFAULT_ALGORITHM_IDS];
    if (typeof window !== 'undefined') {
        try {
            localStorage.setItem('qlc_matrix_algo_favorites', JSON.stringify(visibleAlgorithmIds.value));
        }
        catch {
            // Ignore localStorage errors
        }
    }
}

// Keep activeAlgorithmId in sync with store/websocket updates
watch(
    () => qlcStore.animationStates[props.widgetId]?.algorithmId,
    (newAlgoId) => {
        if (newAlgoId !== undefined) {
            activeAlgorithmId.value = newAlgoId;
        }
    },
    { immediate: true },
);

// Open native color picker for slot 1
function triggerColorPicker1(): void {
    color1InputRef.value?.click();
}

// Open native color picker for slot 2
function triggerColorPicker2(): void {
    color2InputRef.value?.click();
}

// On manual color picker input change
function handleCustomColorChange(): void {
    activeThemeId.value = '';
    applyColors();
}

// Whether the matrix (or any selected target matrix) is currently running / active
const isActive = computed(() => {
    const targets = selectedTargetIds.value.length > 0 ? selectedTargetIds.value : [props.widgetId];
    return targets.some(id => (qlcStore.widgets[id]?.value ?? 0) > 0);
});

// Toggle power on/off for all selected matrices
function handleTogglePower(): void {
    const targets = selectedTargetIds.value.length > 0 ? selectedTargetIds.value : [props.widgetId];
    const willTurnOn = !isActive.value;
    for (const id of targets) {
        setMatrixPower(id, willTurnOn);
    }
}

onMounted(() => {
    // Load custom favorite algorithms from localStorage if available
    if (typeof window !== 'undefined') {
        try {
            const saved = localStorage.getItem('qlc_matrix_algo_favorites');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    visibleAlgorithmIds.value = parsed.map(n => Number(n)).filter(n => !Number.isNaN(n));
                }
            }
        }
        catch {
            // Ignore localStorage parse error
        }
    }

    // If store already has animation state for primary widget, sync initial colors & algorithm
    const existing = qlcStore.animationStates[props.widgetId];
    if (existing) {
        if (existing.primaryColor) {
            color1.value = existing.primaryColor;
        }
        if (existing.secondaryColor) {
            color2.value = existing.secondaryColor;
        }
        if (existing.algorithmId !== undefined) {
            activeAlgorithmId.value = existing.algorithmId;
        }
    }
});
</script>

<template>
    <v-card
        color="primary"
        variant="outlined"
        rounded="lg"
        class="w-100"
        :title="name"
    >
        <v-card-text>
            <!-- Multi-matrix sync selector -->
            <div
                v-if="availableMatrices.length > 1"
                class="multi-sync-section"
            >
                <span class="sync-label">GESYNCHRONISEERDE MATRICES:</span>
                <div class="sync-chips-row">
                    <button
                        v-for="matrix in availableMatrices"
                        :key="matrix.id"
                        type="button"
                        class="sync-chip"
                        :class="{ 'sync-chip--active': selectedTargetIds.includes(matrix.id) }"
                        @click="toggleTarget(matrix.id)"
                    >
                        <v-icon
                            :icon="selectedTargetIds.includes(matrix.id) ? 'mdi-check-circle' : 'mdi-plus-circle-outline'"
                            size="13"
                        />
                        <span>{{ matrix.name }} (ID: {{ matrix.id }})</span>
                    </button>
                </div>
            </div>
            <!-- Master Dual Color Slots -->
            <div class="slots-container w-100">
                <!-- Slot 1: Primair -->
                <v-card
                    class="flex-1-1"
                    @click="triggerColorPicker1"
                >
                    <div class="d-flex align-center ga-3 pa-2 border-grey border-sm rounded-lg">
                        <div class="slot-info flex-1-1">
                            <span class="slot-hex">{{ color1.toUpperCase() }}</span>
                        </div>
                        <div
                            class="color-preview"
                            :style="{ backgroundColor: color1 }"
                        >
                            <v-icon
                                class="edit-icon"
                                icon="mdi-eyedropper"
                                size="14"
                            />
                        </div>
                        <input
                            ref="color1InputRef"
                            v-model="color1"
                            type="color"
                            class="hidden-picker"
                            @input="handleCustomColorChange"
                        >
                    </div>
                </v-card>

                <!-- Swap Button -->
                <v-btn
                    title="Wissel Kleur 1 en 2 om"
                    icon="mdi-swap-horizontal"
                    variant="outlined"
                    rounded="lg"
                    @click="handleSwapColors"
                >
                    <v-icon icon="mdi-swap-horizontal" />
                </v-btn>

                <!-- Slot 2: Secundair -->
                <v-card
                    class="flex-1-1"
                    @click="triggerColorPicker2"
                >
                    <div class="d-flex align-center ga-3 pa-2 border-grey border-sm rounded-lg">
                        <div class="slot-info flex-1-1">
                            <span class="slot-hex">{{ color2.toUpperCase() }}</span>
                        </div>
                        <div
                            class="color-preview"
                            :style="{ backgroundColor: color2 }"
                        >
                            <v-icon
                                class="edit-icon"
                                icon="mdi-eyedropper"
                                size="14"
                            />
                        </div>
                        <input
                            ref="color1InputRef"
                            v-model="color2"
                            type="color"
                            class="hidden-picker"
                            @input="handleCustomColorChange"
                        >
                    </div>
                </v-card>
            </div>
        </v-card-text>
        <v-card-text>
            <div class="text-subtitle-2">THEMA DUO'S</div>
            <div class="themes-grid">
                <v-btn
                    v-for="theme in themes"
                    :key="theme.id"
                    class="theme-pill"
                    :class="{ 'theme-pill--active': activeThemeId === theme.id }"
                    :text="theme.name"
                    size="large"
                    @click="handleSelectTheme(theme)"
                >
                    <template #prepend>
                        <div class="theme-duo-indicator">
                            <span
                                class="duo-half"
                                :style="{ backgroundColor: theme.color1 }"
                            />
                            <span
                                class="duo-half"
                                :style="{ backgroundColor: theme.color2 }"
                            />
                        </div>
                    </template>
                </v-btn>
            </div>
        </v-card-text>
        <v-card-text>
            <div class="text-subtitle-2">EFFEN KLEUREN</div>
            <div class="solid-colors-grid">
                <v-btn
                    v-for="solid in solidColors"
                    :key="solid.hex"
                    :color="solid.hex"
                    :text="solid.name"
                    :style="{ opacity: color1 === solid.hex && color2 === solid.hex ? 1 : .5 }"
                    @click="handleSelectSolidColor(solid.hex)"
                />
            </div>
        </v-card-text>
        <v-card-text>
            <!-- Matrix Pattern / Algorithm Selector -->
            <div class="d-flex mb-2">
                <div class="text-subtitle-2">MATRIX EFFECT PATROON</div>
                <v-spacer />
                <v-btn
                    type="button"
                    color="primary"
                    variant="outlined"
                    :text="`Beheer (${visibleAlgorithmIds.length})`"
                    title="Beheer snelkeuze animaties"
                    prepend-icon="mdi-tune"
                    @click="showAlgoDialog = true"
                />
            </div>

            <div class="algo-grid">
                <v-btn
                    v-for="algoId in visibleAlgorithmIds"
                    :key="algoId"
                    :color="activeAlgorithmId === algoId ? 'primary' : 'grey'"
                    :variant="activeAlgorithmId === algoId ? 'flat' : 'outlined'"
                    @click="handleSelectAlgorithm(algoId)"
                >
                    {{ getAlgorithmById(algoId)?.name || `ID ${algoId}` }}
                </v-btn>
            </div>
        </v-card-text>

        <v-card-actions>
            <v-btn
                size="large"
                block
                :color="isActive ? 'success' : 'grey-darken-3'"
                :variant="isActive ? 'flat' : 'outlined'"
                prepend-icon="mdi-power"
                :class="{ 'power-btn--active': isActive }"
                @click="handleTogglePower"
            >
                {{ isActive ? 'AAN' : 'UIT' }}
            </v-btn>
        </v-card-actions>

        <!-- Algorithm Management Dialog (64 effects catalogue) -->
        <v-dialog
            v-model="showAlgoDialog"
            max-width="580"
            scrollable
        >
            <div class="algo-dialog-card">
                <div class="algo-dialog-header">
                    <div class="algo-dialog-title">
                        <v-icon
                            icon="mdi-animation-play-outline"
                            color="#00f0ff"
                        />
                        <span>MATRIX EFFECTEN BEHEER</span>
                    </div>
                    <button
                        type="button"
                        class="dialog-close-btn"
                        @click="showAlgoDialog = false"
                    >
                        <v-icon
                            icon="mdi-close"
                            size="18"
                        />
                    </button>
                </div>

                <div class="algo-dialog-subtitle">
                    Kies welke effecten op de knoppenbalk staan (ster-icoon), of klik direct op een effect om het live te activeren.
                </div>

                <!-- Search Input -->
                <div class="algo-search-box">
                    <v-icon
                        icon="mdi-magnify"
                        size="18"
                        color="rgba(255,255,255,0.5)"
                    />
                    <input
                        v-model="algoSearchQuery"
                        type="text"
                        placeholder="Zoek in 64 effecten (bijv. Waves, Fire, Strobe)..."
                        class="algo-search-input"
                    >
                    <button
                        v-if="algoSearchQuery"
                        type="button"
                        class="clear-search-btn"
                        @click="algoSearchQuery = ''"
                    >
                        <v-icon
                            icon="mdi-close-circle"
                            size="16"
                        />
                    </button>
                </div>

                <!-- Algorithms List -->
                <div class="algo-dialog-list">
                    <div
                        v-for="algo in filteredAlgorithms"
                        :key="algo.id"
                        class="algo-dialog-item"
                        :class="{
                            'algo-dialog-item--active': activeAlgorithmId === algo.id,
                            'algo-dialog-item--favorite': visibleAlgorithmIds.includes(algo.id),
                        }"
                    >
                        <button
                            type="button"
                            class="algo-fav-toggle"
                            :title="visibleAlgorithmIds.includes(algo.id) ? 'Verwijder uit snelknoppen' : 'Toevoegen aan snelknoppen'"
                            @click="toggleFavoriteAlgorithm(algo.id)"
                        >
                            <v-icon
                                :icon="visibleAlgorithmIds.includes(algo.id) ? 'mdi-star' : 'mdi-star-outline'"
                                :color="visibleAlgorithmIds.includes(algo.id) ? '#ffd600' : 'rgba(255,255,255,0.3)'"
                                size="20"
                            />
                        </button>

                        <button
                            type="button"
                            class="algo-select-btn"
                            @click="handleSelectAlgorithm(algo.id)"
                        >
                            <span class="algo-id-tag">#{{ algo.id }}</span>
                            <span class="algo-item-name">{{ algo.name }}</span>
                            <span
                                v-if="activeAlgorithmId === algo.id"
                                class="algo-live-badge"
                            >
                                ACTIEF
                            </span>
                        </button>
                    </div>
                </div>

                <!-- Dialog Footer -->
                <div class="algo-dialog-footer">
                    <button
                        type="button"
                        class="algo-reset-btn"
                        @click="resetDefaultAlgorithms"
                    >
                        <v-icon
                            icon="mdi-restore"
                            size="14"
                        />
                        <span>Herstel Standaard (6)</span>
                    </button>
                    <button
                        type="button"
                        class="algo-done-btn"
                        @click="showAlgoDialog = false"
                    >
                        Klaar
                    </button>
                </div>
            </div>
        </v-dialog>
    </v-card>
</template>

<style scoped>
.global-color-card {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 14px;
    background: #141424;
    border: 2px solid var(--theme-accent);
    border-radius: 18px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.45), 0 0 14px rgba(255, 0, 127, 0.18);
    gap: 12px;
}

.card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 8px;
}

.header-icon {
    color: var(--theme-accent);
    font-size: 22px;
}

.card-title {
    font-size: 14px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #ffffff;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 6px;
}

.targets-badge {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    font-weight: 800;
    color: #00f0ff;
    background: rgba(0, 240, 255, 0.12);
    padding: 2px 7px;
    border-radius: 6px;
    border: 1px solid rgba(0, 240, 255, 0.25);
}

.global-badge {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1px;
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(255, 0, 127, 0.18);
    color: var(--theme-accent);
    border: 1px solid rgba(255, 0, 127, 0.3);
}

.multi-sync-section {
    display: flex;
    flex-direction: column;
    gap: 5px;
    background: #0d0d18;
    padding: 7px 10px;
    border-radius: 10px;
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.sync-label {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.5);
}

.sync-chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.sync-chip {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 3px 8px;
    border-radius: 6px;
    background: #18182b;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.6);
    font-size: 10px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.12s ease;
}

.sync-chip:hover {
    background: rgba(255, 255, 255, 0.1);
}

.sync-chip--active {
    background: rgba(0, 240, 255, 0.15);
    border-color: #00f0ff;
    color: #00f0ff;
}

.slots-container {
    display: flex;
    align-items: center;
    gap: 8px;
}

.color-slot {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #0d0d18;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.12s ease;
    user-select: none;
}

.color-slot:hover {
    border-color: rgba(255, 255, 255, 0.3);
    background: #121222;
}

.slot-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.slot-label {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.6);
}

.slot-hex {
    font-size: 14px;
    font-weight: 900;
    font-family: monospace;
    color: #ffffff;
}

.color-preview {
    position: relative;
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: 2px solid #ffffff;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
}

.edit-icon {
    color: #000000;
    filter: drop-shadow(0 0 2px #ffffff);
}

.hidden-picker {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
}

.swap-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: #1b1b2d;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.12s ease;
}

.swap-btn:hover {
    background: rgba(255, 255, 255, 0.15);
}

.swap-btn:active {
    transform: scale(0.92);
}

.section-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.section-title {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.5px;
    color: rgba(255, 255, 255, 0.5);
}

.themes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 6px;
}

.theme-pill {
    border: 1px solid rgba(255, 255, 255, 0.12);
}

.theme-pill:hover {
    background: rgba(255, 255, 255, 0.08);
}

.theme-pill--active {
    background: rgba(255, 0, 127, 0.22);
    border-color: var(--theme-accent);
    color: #ffffff;
}

.theme-duo-indicator {
    display: flex;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid rgba(255, 255, 255, 0.4);
}

.duo-half {
    flex: 1;
    height: 100%;
}

.theme-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.solid-colors-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(75px, 1fr));
    gap: 5px;
}
.section-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.manage-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    background: rgba(0, 240, 255, 0.1);
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 6px;
    color: #00f0ff;
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.12s ease;
}

.manage-btn:hover {
    background: rgba(0, 240, 255, 0.2);
    border-color: #00f0ff;
}

.manage-btn:active {
    transform: scale(0.96);
}

.algo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 6px;
}

.algo-pill {
    padding: 8px 6px;
    background: #18182b;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    font-weight: 800;
    cursor: pointer;
    text-align: center;
    transition: all 0.12s ease;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.algo-pill:hover {
    background: rgba(255, 255, 255, 0.08);
}

.algo-pill:active {
    transform: scale(0.95);
}

.algo-pill--active {
    background: #00f0ff;
    border-color: #ffffff;
    color: #000000;
    font-weight: 900;
    box-shadow: 0 0 10px #00f0ff;
}

.power-section {
    margin-top: 4px;
}

.power-btn {
    font-weight: 800;
    letter-spacing: 1.5px;
    border-radius: 12px;
}

.power-btn--active {
    box-shadow: 0 0 16px rgba(76, 175, 80, 0.4);
}

/* Modal Dialog Styles */
.algo-dialog-card {
    display: flex;
    flex-direction: column;
    background: #141424;
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 16px;
    padding: 16px;
    gap: 12px;
    max-height: 80vh;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.7), 0 0 20px rgba(0, 240, 255, 0.15);
}

.algo-dialog-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.algo-dialog-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.5px;
}

.dialog-close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: all 0.12s ease;
}

.dialog-close-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
}

.algo-dialog-subtitle {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.4;
}

.algo-search-box {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: #0d0d18;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 10px;
}

.algo-search-box:focus-within {
    border-color: #00f0ff;
    box-shadow: 0 0 8px rgba(0, 240, 255, 0.25);
}

.algo-search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
}

.clear-search-btn {
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    display: flex;
    align-items: center;
}

.clear-search-btn:hover {
    color: #ffffff;
}

.algo-dialog-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow-y: auto;
    max-height: 50vh;
    padding-right: 4px;
}

.algo-dialog-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: #18182b;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    transition: all 0.12s ease;
}

.algo-dialog-item:hover {
    background: #1f1f38;
    border-color: rgba(255, 255, 255, 0.2);
}

.algo-dialog-item--favorite {
    border-color: rgba(255, 214, 0, 0.35);
    background: rgba(255, 214, 0, 0.04);
}

.algo-dialog-item--active {
    border-color: #00f0ff;
    background: rgba(0, 240, 255, 0.12);
}

.algo-fav-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 2px;
}

.algo-fav-toggle:hover {
    transform: scale(1.15);
}

.algo-select-btn {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    border: none;
    color: #ffffff;
    cursor: pointer;
    text-align: left;
    padding: 4px 0;
}

.algo-id-tag {
    font-size: 11px;
    font-weight: 700;
    font-family: monospace;
    color: rgba(255, 255, 255, 0.4);
    min-width: 32px;
}

.algo-item-name {
    font-size: 13px;
    font-weight: 700;
    flex: 1;
}

.algo-live-badge {
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.5px;
    padding: 2px 6px;
    border-radius: 4px;
    background: #00f0ff;
    color: #000000;
}

.algo-dialog-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 8px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.algo-reset-btn {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.12s ease;
}

.algo-reset-btn:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
}

.algo-done-btn {
    padding: 6px 18px;
    background: #00f0ff;
    border: none;
    border-radius: 8px;
    color: #000000;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.12s ease;
}

.algo-done-btn:hover {
    background: #33f3ff;
    box-shadow: 0 0 10px rgba(0, 240, 255, 0.4);
}
</style>
